import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { addPosition } from '../../../../redux/Slices/PositionsSlice';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #3a4050',
  borderRadius: '8px',
  color: 'white',
  fontSize: '14px',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#ccc',
  fontWeight: '500',
  fontSize: '14px',
};

const AddJobTitle = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error, success } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: ''
  });
  const lastErrorRef = useRef({ message: null, time: 0 });

  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.description_en || !formData.description_ar) {
      toast.error(t('jobTitles.addForm.requiredFields', { defaultValue: 'جميع الحقول مطلوبة' }), { rtl: isRTL });
      return;
    }
    await dispatch(addPosition({ positionData: formData, language: i18n.language }));
  };

  React.useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      setTimeout(() => {
        navigate('/job-titles');
        dispatch(clearSuccess());
      }, 1500);
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign,
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333',
        }}>
          {/* Card Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: 'white' }}>
              {t('jobTitles.addForm.title', { defaultValue: 'إضافة مسمى وظيفي جديد' })}
            </h1>
            <button
              type="button"
              onClick={() => navigate('/job-titles')}
              style={{
                backgroundColor: '#6c757d',
                border: 'none',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                borderRadius: '8px',
                fontWeight: 'bold',
                flexDirection: isRTL ? 'row-reverse' : 'row',
              }}
            >
              <FaArrowLeft />
              {t('jobTitles.addForm.back', { defaultValue: 'رجوع' })}
            </button>
          </div>

          {/* Card Body */}
          <div style={{ padding: '28px 24px' }}>
            <form onSubmit={handleSubmit}>
              {/* Names row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '20px',
              }}>
                <div>
                  <label style={labelStyle}>
                    {t('jobTitles.addForm.nameAr', { defaultValue: 'الاسم بالعربية' })}{' '}
                    <span style={{ color: '#AC2000' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={formData.name_ar}
                    onChange={handleInputChange}
                    placeholder={t('jobTitles.addForm.nameArPlaceholder', { defaultValue: 'اسم المسمى الوظيفي' })}
                    required
                    style={{ ...inputStyle, direction: 'rtl', textAlign: 'right' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    {t('jobTitles.addForm.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}{' '}
                    <span style={{ color: '#AC2000' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={formData.name_en}
                    onChange={handleInputChange}
                    placeholder={t('jobTitles.addForm.nameEnPlaceholder', { defaultValue: 'Position Name in English' })}
                    required
                    style={{ ...inputStyle, direction: 'ltr', textAlign: 'left' }}
                  />
                </div>
              </div>

              {/* Descriptions row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                marginBottom: '28px',
              }}>
                <div>
                  <label style={labelStyle}>
                    {t('jobTitles.addForm.descriptionAr', { defaultValue: 'الوصف بالعربية' })}{' '}
                    <span style={{ color: '#AC2000' }}>*</span>
                  </label>
                  <textarea
                    name="description_ar"
                    value={formData.description_ar}
                    onChange={handleInputChange}
                    placeholder={t('jobTitles.addForm.descriptionArPlaceholder', { defaultValue: 'وصف المسمى الوظيفي' })}
                    required
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      lineHeight: '1.6',
                      direction: 'rtl',
                      textAlign: 'right',
                    }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    {t('jobTitles.addForm.descriptionEn', { defaultValue: 'الوصف بالإنجليزية' })}{' '}
                    <span style={{ color: '#AC2000' }}>*</span>
                  </label>
                  <textarea
                    name="description_en"
                    value={formData.description_en}
                    onChange={handleInputChange}
                    placeholder={t('jobTitles.addForm.descriptionEnPlaceholder', { defaultValue: 'Position description in English' })}
                    required
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      lineHeight: '1.6',
                      direction: 'ltr',
                      textAlign: 'left',
                    }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                borderTop: '1px solid #333',
                paddingTop: '24px',
              }}>
                <button
                  type="button"
                  onClick={() => navigate('/job-titles')}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '11px 28px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  {t('jobTitles.addForm.cancel', { defaultValue: 'إلغاء' })}
                </button>

                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? '#666' : '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '11px 28px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FaPlus />
                  {isLoading
                    ? t('jobTitles.addForm.saving', { defaultValue: 'جاري الإضافة...' })
                    : t('jobTitles.addForm.save', { defaultValue: 'إضافة' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddJobTitle;
